import { Component, ChangeDetectionStrategy, input, output, computed, signal, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Select } from '@openng/optimus-ui/select';

/**
 * A DTC filter with two linked dropdowns, ECU and code. Either can be used alone or together:
 *   - ECU only  → suites where that ECU reported any DTC
 *   - code only → suites where that code was reported by any ECU
 *   - both      → that code reported on that ECU (an exact, real (ecu, code) pair — not just the code
 *     somewhere and the ECU somewhere)
 *
 * When an ECU is selected the code dropdown cascades to that ECU's codes; with no ECU it offers all codes.
 *
 * It is driven by ONE input — the distinct "ECU:code" pairs (the dtc_pair facet). BOTH dropdowns derive
 * from it: the ECU dropdown is the distinct "ECU:" prefixes, and the code dropdown is the codes of the
 * pairs whose prefix matches the selected ECU. The chosen (ecu, code) is emitted back as the exact
 * "ECU:code" value, which the parent sets as the dtc_pair filter (native array-membership match
 * server-side). No separate ECU facet is needed.
 *
 * The component is presentational: it takes the raw option lists and the current pending dtc_pair value,
 * and emits the new dtc_pair value (or null to clear). The parent owns the pending-filter state and the
 * Apply flow, exactly like the generic property panels.
 *
 * The ECU selection is held LOCALLY (selectedEcu), not derived from the emitted pair: picking an ECU is a
 * partial, not-yet-complete selection (no pair is emitted until a code is also chosen), so it must persist
 * on its own to drive the code dropdown. selectedEcu is seeded from the incoming pair so an
 * already-applied filter shows both halves when the panel reopens.
 */
@Component({
	selector: 'app-dtc-filter',
	imports: [FormsModule, Select],
	templateUrl: './dtc-filter.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './dtc-filter.component.scss',
})
export class DtcFilterComponent {
	/** Distinct "ECU:code" pairs (the dtc_pair facet members) — the single source for BOTH dropdowns. */
	readonly pairs = input.required<string[]>();

	/** The currently applied/pending dtc_pair value ("ECU:code"), or null when no DTC filter is set. */
	readonly selectedPair = input<string | null>(null);

	/**
	 * Emits the DTC filter value, encoded by which halves are set (the backend branches on the colon):
	 *   - "ECU:code" both  → that code on that ECU (exact)
	 *   - "ECU:"     ecu   → any code on that ECU
	 *   - ":code"    code  → that code on any ECU
	 *   - null       none  → clear
	 * Either dropdown may be used alone — a code is NOT required.
	 */
	readonly pairChange = output<string | null>();

	/** Locally-held ECU and code halves. Either can be set independently; the emitted value is derived
	 *  from both (emit() below). Held locally because a partial selection isn't a complete applied pair. */
	readonly selectedEcu = signal<string | null>(null);

	readonly selectedCode = signal<string | null>(null);

	// Seed/resync both halves from the incoming applied value, so reopening the panel (or an external
	// clear) reflects the real state. Decodes the same encoding emit() produces: "ECU:code" / "ECU:" /
	// ":code". Reads only selectedPair; writes to separate signals so it doesn't loop.
	private syncFromPair = effect(() => {
		const pair = this.selectedPair();
		if (!pair) {
			this.selectedEcu.set(null);
			this.selectedCode.set(null);
			return;
		}
		const idx = pair.indexOf(':');
		if (idx < 0) {
			this.selectedEcu.set(pair);
			this.selectedCode.set(null);
			return;
		}
		this.selectedEcu.set(pair.slice(0, idx) || null);
		this.selectedCode.set(pair.slice(idx + 1) || null);
	});

	/** ECU dropdown options: the distinct "ECU:" prefixes of the pairs, sorted. Derived from `pairs` so no
	 * separate ECU facet is needed — an ECU with no codes has no pair and so (correctly) isn't offered. */
	readonly ecuOptions = computed<string[]>(() => {
		const ecus = new Set<string>();
		for (const p of this.pairs()) {
			const idx = p.indexOf(':');
			if (idx > 0) ecus.add(p.slice(0, idx));
		}
		return [...ecus].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
	});

	/**
	 * Code dropdown options: when an ECU is selected, the codes reported by THAT ECU (cascade); otherwise
	 * ALL distinct codes across every ECU (so a code can be chosen alone, for "that code on any ECU").
	 */
	readonly codeOptions = computed<string[]>(() => {
		const ecu = this.selectedEcu();
		const codes = new Set<string>();
		for (const p of this.pairs()) {
			const idx = p.indexOf(':');
			if (idx < 0) continue;
			const pairEcu = p.slice(0, idx);
			if (ecu && pairEcu !== ecu) continue;
			codes.add(p.slice(idx + 1));
		}
		return [...codes].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
	});

	/**
	 * Handles an ECU change. If a code was already chosen but isn't reported by the new ECU, it's cleared
	 * so the selection stays consistent (the code dropdown cascades to the ECU). Then re-emit.
	 * @param ecu
	 */
	public onEcuChange(ecu: string | null): void {
		this.selectedEcu.set(ecu);
		const code = this.selectedCode();
		if (ecu && code && !this.pairs().includes(`${ecu}:${code}`)) {
			this.selectedCode.set(null);
		}
		this.emit();
	}

	/**
	 * Handles a code change, then re-emit.
	 * @param code
	 */
	public onCodeChange(code: string | null): void {
		this.selectedCode.set(code);
		this.emit();
	}

	/**
	 * Emits the encoded filter value from the current ECU/code halves (or null when both are empty).
	 * "ECU:code" when both set, "ECU:" for ecu-only, ":code" for code-only.
	 */
	private emit(): void {
		const ecu = this.selectedEcu();
		const code = this.selectedCode();
		if (!ecu && !code) {
			this.pairChange.emit(null);
			return;
		}
		this.pairChange.emit(`${ecu ?? ''}:${code ?? ''}`);
	}
}
